'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import { useTranslations } from 'next-intl';
import { useToast } from '@boty/ui';
import {
  useIntakeQuestions,
  useCreateIntakeQuestion,
  useBulkUpdateIntakeQuestions,
  useDeleteIntakeQuestion,
  useUpdateIntakeSettings,
} from './useIntakeQuestions';
import type { IntakeQuestionApi } from './types';

const MAX_QUESTIONS = 10;

const SUGGESTED_QUESTION_KEYS = ['suggestedName', 'suggestedEmail', 'suggestedPhone'] as const;

interface IntakeQuestionsConfigProps {
  botId: string;
  intakeEnabled: boolean;
  intakeCompletionMessage: string | null;
  intakePromptMessage: string | null;
}

export function IntakeQuestionsConfig({
  botId,
  intakeEnabled,
  intakeCompletionMessage,
  intakePromptMessage,
}: IntakeQuestionsConfigProps) {
  const t = useTranslations('creator.intakeQuestions');
  const toast = useToast();
  const updateIntakeSettings = useUpdateIntakeSettings(botId);
  const { data: questionsData, isPending: isLoading } = useIntakeQuestions(botId);
  const createQuestion = useCreateIntakeQuestion(botId);
  const bulkUpdate = useBulkUpdateIntakeQuestions(botId);
  const deleteQuestionMutation = useDeleteIntakeQuestion(botId);

  const [localEnabled, setLocalEnabled] = useState(intakeEnabled);
  const [completionMsg, setCompletionMsg] = useState(intakeCompletionMessage || '');
  const [promptMsg, setPromptMsg] = useState(intakePromptMessage || '');
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const promptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Local draft question (not yet saved to server)
  const [draftText, setDraftText] = useState<string | null>(null);
  const draftInputRef = useRef<HTMLInputElement>(null);
  const draftSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Drag-and-drop state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const questions = questionsData?.data || [];

  // Sync with external changes — but skip fields if a save is pending
  useEffect(() => {
    setLocalEnabled(intakeEnabled);
    if (!completionTimerRef.current) {
      setCompletionMsg(intakeCompletionMessage || '');
    }
    if (!promptTimerRef.current) {
      setPromptMsg(intakePromptMessage || '');
    }
  }, [intakeEnabled, intakeCompletionMessage, intakePromptMessage]);

  const handleToggle = useCallback(
    (enabled: boolean) => {
      setLocalEnabled(enabled);
      updateIntakeSettings.mutate(
        { intakeEnabled: enabled },
        {
          onSuccess: () => toast.success(t('saved')),
          onError: (err) => toast.error(err.message),
        }
      );
    },
    [updateIntakeSettings, toast, t]
  );

  const handleCompletionChange = useCallback(
    (value: string) => {
      setCompletionMsg(value);
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
      completionTimerRef.current = setTimeout(() => {
        completionTimerRef.current = null;
        updateIntakeSettings.mutate(
          { intakeCompletionMessage: value || null },
          {
            onSuccess: () => toast.success(t('saved')),
            onError: (err) => toast.error(err.message),
          }
        );
      }, 2500);
    },
    [updateIntakeSettings, toast, t]
  );

  const handlePromptChange = useCallback(
    (value: string) => {
      setPromptMsg(value);
      if (promptTimerRef.current) clearTimeout(promptTimerRef.current);
      promptTimerRef.current = setTimeout(() => {
        promptTimerRef.current = null;
        updateIntakeSettings.mutate(
          { intakePromptMessage: value || null },
          {
            onSuccess: () => toast.success(t('saved')),
            onError: (err) => toast.error(err.message),
          }
        );
      }, 2500);
    },
    [updateIntakeSettings, toast, t]
  );

  useEffect(() => {
    return () => {
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
      if (promptTimerRef.current) clearTimeout(promptTimerRef.current);
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    };
  }, []);

  // Save draft question to server
  const saveDraft = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      setDraftText(null);
      // Auto-enable intake when adding first question
      if (!localEnabled) {
        handleToggle(true);
      }
      createQuestion.mutate(text.trim(), {
        onError: (err) => toast.error(err.message),
      });
    },
    [createQuestion, toast, localEnabled, handleToggle]
  );

  const handleAddQuestion = useCallback(() => {
    if (questions.length + (draftText !== null ? 1 : 0) >= MAX_QUESTIONS) {
      toast.error(t('maxQuestionsReached'));
      return;
    }
    setDraftText('');
    // Focus the draft input after render
    setTimeout(() => draftInputRef.current?.focus(), 50);
  }, [questions.length, draftText, toast, t]);

  const handleAddSuggested = useCallback(
    (text: string) => {
      if (questions.length >= MAX_QUESTIONS) {
        toast.error(t('maxQuestionsReached'));
        return;
      }
      // Auto-enable intake when adding first question
      if (!localEnabled) {
        handleToggle(true);
      }
      createQuestion.mutate(text, {
        onError: (err) => toast.error(err.message),
      });
    },
    [questions.length, createQuestion, toast, t, localEnabled, handleToggle]
  );

  const handleDraftChange = useCallback(
    (value: string) => {
      setDraftText(value);
      // Auto-save draft after debounce
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
      if (value.trim()) {
        draftSaveTimerRef.current = setTimeout(() => saveDraft(value), 1500);
      }
    },
    [saveDraft]
  );

  const handleDraftKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && draftText?.trim()) {
        e.preventDefault();
        if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
        saveDraft(draftText);
      } else if (e.key === 'Escape') {
        if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
        setDraftText(null);
      }
    },
    [draftText, saveDraft]
  );

  const handleDraftBlur = useCallback(() => {
    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    if (draftText?.trim()) {
      saveDraft(draftText);
    } else {
      setDraftText(null);
    }
  }, [draftText, saveDraft]);

  const handleDeleteQuestion = useCallback(
    (questionId: string) => {
      deleteQuestionMutation.mutate(questionId, {
        onError: (err) => toast.error(err.message),
      });
    },
    [deleteQuestionMutation, toast]
  );

  // Drag-and-drop handlers
  const handleDragStart = useCallback((index: number) => {
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const updated = [...questions];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(dragOverIndex, 0, moved!);
      bulkUpdate.mutate(
        updated.map((q, i) => ({
          id: q.id,
          questionText: q.questionText,
          sortOrder: i,
        }))
      );
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, dragOverIndex, questions, bulkUpdate]);

  const handleQuestionTextChange = useCallback(
    (question: IntakeQuestionApi, newText: string) => {
      const withOrders = questions.map((q) => ({
        id: q.id,
        questionText: q.id === question.id ? newText : q.questionText,
        sortOrder: q.sortOrder,
      }));
      bulkUpdate.mutate(withOrders);
    },
    [questions, bulkUpdate]
  );

  // Filter suggested questions that aren't already in the list
  const availableSuggestions = SUGGESTED_QUESTION_KEYS.filter(
    (key) => !questions.some((q) => q.questionText.toLowerCase() === t(key).toLowerCase())
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('sectionDescription')}
      </Typography>

      {/* Questions List */}
      {questions.map((question, index) => (
        <QuestionRow
          key={question.id}
          question={question}
          index={index}
          isDragging={dragIndex === index}
          isDragOver={dragOverIndex === index}
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          onDelete={() => handleDeleteQuestion(question.id)}
          onTextChange={(text) => handleQuestionTextChange(question, text)}
        />
      ))}

      {/* Draft question (new, not yet saved) */}
      {draftText !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.8 }}>
          <Box sx={{ width: 28 }} /> {/* Spacer matching drag handle */}
          <TextField
            inputRef={draftInputRef}
            value={draftText}
            onChange={(e) => handleDraftChange(e.target.value)}
            onKeyDown={handleDraftKeyDown}
            onBlur={handleDraftBlur}
            fullWidth
            size="small"
            placeholder={t('questionPlaceholder')}
            inputProps={{ maxLength: 500 }}
            autoFocus
          />
          <IconButton size="small" onClick={() => setDraftText(null)} color="error">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Suggested Questions */}
      {availableSuggestions.length > 0 && questions.length < MAX_QUESTIONS && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('suggestedQuestionsLabel')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {availableSuggestions.map((key) => (
              <Chip
                key={key}
                label={t(key)}
                onClick={() => handleAddSuggested(t(key))}
                variant="outlined"
                size="small"
                disabled={createQuestion.isPending}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Add Question Button */}
      {questions.length + (draftText !== null ? 1 : 0) < MAX_QUESTIONS && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddQuestion}
          disabled={createQuestion.isPending || draftText !== null}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('addQuestion')}
        </Button>
      )}

      {questions.length >= MAX_QUESTIONS && (
        <Alert severity="info" variant="outlined">
          {t('maxQuestionsReached')}
        </Alert>
      )}

      {/* Prompt Message, Completion Message, and Disable Toggle — only shown when questions exist */}
      {questions.length > 0 && (
        <>
          <TextField
            label={t('promptMessage')}
            value={promptMsg}
            onChange={(e) => handlePromptChange(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
            InputLabelProps={{ shrink: true }}
            placeholder={t('promptMessagePlaceholder')}
            helperText={promptMsg.length > 400 ? `${promptMsg.length}/500` : t('promptMessageHelp')}
            sx={{ mt: 1 }}
          />

          <TextField
            label={t('completionMessage')}
            value={completionMsg}
            onChange={(e) => handleCompletionChange(e.target.value)}
            fullWidth
            multiline
            rows={2}
            inputProps={{ maxLength: 500 }}
            InputLabelProps={{ shrink: true }}
            placeholder={t('completionMessagePlaceholder')}
            helperText={completionMsg.length > 400 ? `${completionMsg.length}/500` : t('completionMessageHelp')}
            sx={{ mt: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={!localEnabled}
                onChange={(e) => handleToggle(!e.target.checked)}
                disabled={updateIntakeSettings.isPending}
              />
            }
            label={t('disableForNow')}
            sx={{ mt: 1 }}
          />
        </>
      )}
    </Box>
  );
}

// Individual question row component with drag support
function QuestionRow({
  question,
  index,
  isDragging,
  isDragOver,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDelete,
  onTextChange,
}: {
  question: IntakeQuestionApi;
  index: number;
  isDragging: boolean;
  isDragOver: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDelete: () => void;
  onTextChange: (text: string) => void;
}) {
  const t = useTranslations('creator.intakeQuestions');
  const [localText, setLocalText] = useState(question.questionText);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalText(question.questionText);
  }, [question.questionText]);

  const handleChange = (value: string) => {
    setLocalText(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (value.trim()) onTextChange(value);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <Box
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1 },
        opacity: isDragging ? 0.4 : 1,
        borderTop: isDragOver ? '2px solid' : '2px solid transparent',
        borderColor: isDragOver ? 'primary.main' : 'transparent',
        transition: 'opacity 0.15s, border-color 0.15s',
      }}
    >
      {/* Drag handle hidden on mobile — drag-drop is awkward on touch */}
      <Box
        sx={{
          cursor: 'grab',
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          color: 'text.disabled',
          '&:active': { cursor: 'grabbing' },
          touchAction: 'none',
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>

      <Typography variant="caption" color="text.disabled" sx={{ minWidth: 18, textAlign: 'center', flexShrink: 0 }}>
        {index + 1}
      </Typography>

      <TextField
        value={localText}
        onChange={(e) => handleChange(e.target.value)}
        fullWidth
        size="small"
        placeholder={t('questionPlaceholder')}
        inputProps={{ maxLength: 500 }}
        sx={{ minWidth: 0 }}
      />

      <IconButton size="small" onClick={onDelete} color="error">
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
